import { z } from 'zod';
import { apiGet, apiPost, apiSend } from '@/lib/api';
import type { FormatPliku, NazwaRaportu } from '@/lib/raporty-api';

/**
 * HARMONOGRAMY WYSYŁKI RAPORTÓW.
 *
 * ⚠️ OKRES JEST NAZWĄ, NIE ZAKRESEM DAT — i tak samo wygląda po tej stronie. Ekran nie ma pola
 * „od–do" i mieć nie będzie: zapisany zakres znaczyłby, że tygodniówka wysyła ten sam tydzień
 * do końca świata. Granice liczy serwer przy każdym uruchomieniu (patrz `okres.ts` w API).
 */

export const OKRESY = [
  'wczoraj',
  'ostatnie7dni',
  'ostatnie30dni',
  'poprzedniTydzien',
  'poprzedniMiesiac',
  'biezacyMiesiac',
] as const;
export type Okres = (typeof OKRESY)[number];

export const CZESTOTLIWOSCI = ['DZIENNIE', 'TYGODNIOWO', 'MIESIECZNIE'] as const;
export type Czestotliwosc = (typeof CZESTOTLIWOSCI)[number];

const PrzebiegSchema = z.object({
  id: z.string(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  status: z.enum(['OK', 'BLAD']),
  /** Wyliczony okres — jedyny dowód, CO tamten plik zawierał. */
  periodFrom: z.string().nullable(),
  periodTo: z.string().nullable(),
  rows: z.number().nullable(),
  bytes: z.number().nullable(),
  fileName: z.string().nullable(),
  error: z.string().nullable(),
});
export type Przebieg = z.infer<typeof PrzebiegSchema>;

const HarmonogramSchema = z.object({
  id: z.string(),
  groupId: z.string(),
  name: z.string(),
  report: z.enum(['transactions', 'stanAutomatow']),
  /**
   * ⚠️ `txt` DOŁOŻONE 31.08.2026. Serwer przyjmował ten format od początku, ale schemat klienta go
   * nie znał — czyli harmonogram z plikiem tab delimited nie dałby się nawet wczytać na listę.
   */
  format: z.enum(['csv', 'txt', 'xlsx']),
  /** Czy `.txt` TEGO harmonogramu ma wiersz z nazwami kolumn. Per raport, nie per wyspa. */
  naglowkiTxt: z.boolean(),
  columns: z.array(z.string()),
  period: z.string().nullable(),
  filters: z.unknown().nullable(),
  recipients: z.array(z.string()),
  frequency: z.enum(CZESTOTLIWOSCI),
  hour: z.number(),
  minute: z.number(),
  weekday: z.number().nullable(),
  dayOfMonth: z.number().nullable(),
  active: z.boolean(),
  nextRunAt: z.string(),
  lastRunAt: z.string().nullable(),
  ostatniPrzebieg: PrzebiegSchema.nullable(),
});
export type Harmonogram = z.infer<typeof HarmonogramSchema>;

const ListaSchema = z.object({
  rows: z.array(HarmonogramSchema),
  /**
   * ⚠️ LIMITY PRZYCHODZĄ Z SERWERA, nie są powtórzone tutaj. Druga kopia rozjechałaby się przy
   * pierwszej zmianie i ekran pokazywałby ostrzeżenie „nie zmieści się w mailu" tam, gdzie go nie ma.
   */
  limity: z.object({ bajtowZalacznika: z.number(), wierszy: z.number() }),
});

export type ZapisHarmonogramu = {
  name: string;
  report: NazwaRaportu;
  format: FormatPliku;
  /**
   * Czy `.txt` tego harmonogramu ma wiersz z nazwami kolumn.
   *
   * ⚠️ WYSYŁAMY ZAWSZE, także przy CSV i XLSX (gdzie jest bez znaczenia). Pominięcie znaczyłoby
   * cofnięcie do domyślki po każdym przełączeniu formatu — a decyzja „ten raport jedzie do
   * programu" nie przestaje być prawdą, gdy ktoś na chwilę zajrzy w inny format.
   */
  naglowkiTxt: boolean;
  columns: string[];
  period: Okres | null;
  filtry?: { machineId?: string };
  recipients: string[];
  frequency: Czestotliwosc;
  hour: number;
  minute: number;
  weekday?: number | null;
  dayOfMonth?: number | null;
  active: boolean;
};

export const listaHarmonogramow = (groupId: string) =>
  apiGet(`/reports/harmonogramy?groupId=${groupId}`, ListaSchema);

export const utworzHarmonogram = (groupId: string, dane: ZapisHarmonogramu) =>
  apiPost('/reports/harmonogramy', { groupId, ...dane }, HarmonogramSchema.partial());

export const zmienHarmonogram = (id: string, dane: ZapisHarmonogramu) =>
  apiSend('PATCH', `/reports/harmonogramy/${id}`, dane, HarmonogramSchema.partial());

export const usunHarmonogram = (id: string) =>
  apiSend('DELETE', `/reports/harmonogramy/${id}`, undefined, z.object({ usunieto: z.boolean() }));

/** Uruchomienie próbne — CELOWO nie przesuwa zaplanowanej pory (patrz `uruchomTeraz` w API). */
export const uruchomHarmonogram = (id: string) =>
  apiPost(`/reports/harmonogramy/${id}/uruchom`, {}, PrzebiegSchema);

export const przebiegiHarmonogramu = (id: string) =>
  apiGet(`/reports/harmonogramy/${id}/przebiegi`, z.object({ rows: z.array(PrzebiegSchema) }));
